import * as Service from 'types/service';
import { Button } from '@tock/shared/components/buttons/DeprecatedButton';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { IconChevronSmallRight } from '@tocktix/pantry';
import { PrintChitSource } from 'constants/PrinterConstants';
import { StateSelectorStatus } from 'components/service/party-panel/PartyPanelFooter';
import { WithAsyncRenderProps, withAsyncRender } from 'contexts/asyncRender';
import { getNextStatusIndex } from 'utils/partyUtils';
import { getPartyStateSelectorOptions } from 'utils/partyStateSelectorOptions';
import { handlePartyStatusSelection } from 'utils/partyStatusSelection';
import BaseComponent from '@tock/shared/components/BaseComponent';
import BasicDropdown from '@tock/shared/components/inputs/basic-dropdown/BasicDropdown';
import Field from '@tock/shared/components/forms/Field';
import SecondaryStateItem from 'components/service/dropdown/SecondaryStateItem';
import clsx from 'clsx';

export interface StateSelectorProps {
  handleUpdatePartyState(party: Service.Party, state: string, secondaryState: string | null): void;
  isDisabled: boolean;
  party: Service.Party;
  position?: 'bottom' | 'top';
  printOnSeat: boolean;
  status: StateSelectorStatus;
  secondaryStates?: { [key: string]: Service.StateDescription[] };
  modifierClassName?: string;
  onPrintChit?(party: Service.Party, source: PrintChitSource): void;
}

type Props = StateSelectorProps & IWithContextProps & WithAsyncRenderProps;

interface State {
  options: Service.StateDescription[];
}

class PartyPanelStateSelector extends BaseComponent<Props, State> {
  state: State = {
    options: [],
  };

  static defaultProps = {
    position: 'top' as StateSelectorProps['position'],
  };

  constructor(props: Props) {
    super(props);
  }

  handleUpdatePartyState(option: Service.StateDescription): void {
    const {
      asyncRender,
      context: { isFeatureEnabled },
      handleUpdatePartyState,
      party,
      printOnSeat,
      onPrintChit,
    } = this.props;
    handlePartyStatusSelection({
      party,
      option,
      printOnSeat,
      onPrintChit,
      isPrintPartiallySeatedEnabled: isFeatureEnabled('PRINT_PARTIALLY_SEATED'),
      confirm: asyncRender.confirm,
      onConfirmOrImmediateSelect: () =>
        handleUpdatePartyState(party, option.primaryState, option.secondaryValue),
    });
  }

  // this method extracts the ordering and adds an individual state to certain lists of states depending on the party panel
  static getDerivedStateFromProps(props: Props): State {
    const {
      party,
      status,
      secondaryStates,
      context: { isFeatureEnabled },
    } = props;
    const selectorStatus = status as unknown as Parameters<
      typeof getPartyStateSelectorOptions
    >[0]['status'];
    const partyStatusRefreshEnabled = isFeatureEnabled('PARTY_STATUS_REFRESH');

    if (!secondaryStates) {
      return {
        options: [],
      };
    }
    return {
      options: getPartyStateSelectorOptions({
        party,
        status: selectorStatus,
        secondaryStates,
        partyStatusRefreshEnabled,
      }),
    };
  }

  handleGoToNextStatus() {
    const { party } = this.props;
    const { options } = this.state;

    const nextOptionIndex = getNextStatusIndex(party, options);

    this.handleUpdatePartyState(options[nextOptionIndex]);
  }

  render() {
    const {
      isDisabled,
      party,
      modifierClassName,
      context: { isFeatureEnabled },
    } = this.props;
    const selectedOption = party.stateDescription;
    const isPartyStatusRefresh = isFeatureEnabled('PARTY_STATUS_REFRESH');
    const dropdownClassName = clsx('PartyStateSelector-dropdown', {
      'PartyStateSelector-dropdown--refresh': isPartyStatusRefresh,
    });

    return (
      <Field className={modifierClassName} key="state">
        <div className={'PartyStateSelector-state'} data-testid="party-status">
          <BasicDropdown
            contextClassName={dropdownClassName}
            fitContentWidth={isPartyStatusRefresh}
            component={SecondaryStateItem}
            position={this.props.position}
            disabled={isDisabled}
            hideChevron={true}
            options={this.state.options}
            selectedOption={selectedOption}
            onSelect={this.handleUpdatePartyState}
          />
          <Button
            modifierClassName="PartyStateSelector-next"
            size="small"
            onClick={this.handleGoToNextStatus}
          >
            <IconChevronSmallRight className="PartyStateSelector-next-icon" />
          </Button>
        </div>
      </Field>
    );
  }
}

export default withContext<StateSelectorProps>(withAsyncRender(PartyPanelStateSelector));
